import * as React from "react";

import { cn } from "@/lib/utils";

type SelectProps = {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
};

type SelectContextValue = {
  value: string;
  onValueChange: (value: string) => void;
};

const SelectContext = React.createContext<SelectContextValue | null>(null);

function useSelectContext() {
  const context = React.useContext(SelectContext);
  if (!context) throw new Error("Select components must be used inside <Select>");
  return context;
}

function Select({ value, onValueChange, children }: SelectProps) {
  const itemsRef = React.useRef<React.ReactNode>(null);

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type === SelectContent) {
      const contentChild = child as React.ReactElement<{ children: React.ReactNode }>;
      itemsRef.current = contentChild.props.children;
    }
  });

  const contextValue = React.useMemo(() => ({ value, onValueChange }), [value, onValueChange]);
  return (
    <SelectContext.Provider value={contextValue}>
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;
        if (child.type === SelectTrigger) {
          const triggerChild = child as React.ReactElement<{ children: React.ReactNode }>;
          return React.cloneElement(
            triggerChild,
            {},
            [triggerChild.props.children, itemsRef.current],
          );
        }
        if (child.type === SelectContent) {
          return null;
        }
        return child;
      })}
    </SelectContext.Provider>
  );
}

function SelectTrigger({ className, children, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  const context = useSelectContext();
  return (
    <select
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      value={context.value}
      onChange={(event) => context.onValueChange(event.target.value)}
      {...props}
    >
      {children}
    </select>
  );
}

function SelectValue({ placeholder }: { placeholder?: string }) {
  return <option value="" disabled hidden>{placeholder ?? "Select"}</option>;
}

function SelectContent({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function SelectItem({ value, children }: { value: string; children: React.ReactNode }) {
  return <option value={value}>{children}</option>;
}

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem };
